import type {ReactNode} from 'react';
import {cx} from './cx';

/**
 * A small labelled state. A label, never a control.
 *
 * It has no tap target and takes no `onClick` — deliberately, because the
 * moment a pill becomes pressable it needs 48px, a focus ring and a name, and it
 * stops being the quiet thing the design uses it as. If a state needs to be
 * pressable it is a `Chip` or a `Button`.
 *
 * # Tones
 *
 * * `neutral` — a fact. "Chosen", "3 items", "table 12".
 * * `warning` — Palm Sugar. Needs attention, is not an error: sold out, required
 *   but unanswered, not yet paid.
 * * `info` — Butterfly Pea. Done, but not money: the order reached the kitchen.
 *   This is astryx's generic success and it is blue on purpose.
 * * `verified` — Pandan. **Server-verified money only**, decided by
 *   `src/order/moneyTone.ts` and never by the screen.
 *
 * The design has a pill that turns green the moment a spice level is chosen.
 * That is the one place the design package breaks its own money rule, and it is
 * not ported: a radio button being answered is not verified money. Use `neutral`
 * — or drop the pill, since the filled radio already says it.
 *
 * Colour is never the only carrier of meaning (foundations §12): the label is
 * the state, and the tone only reinforces it.
 */
export type PillTone = 'neutral' | 'warning' | 'info' | 'verified';

export function StatusPill({
  children,
  tone = 'neutral',
  className,
}: {
  /** Localised text. Never an icon on its own. */
  children: ReactNode;
  tone?: PillTone;
  className?: string;
}) {
  return <span className={cx('pk-pill', `pk-pill--${tone}`, className)}>{children}</span>;
}
